/**
 * Read-only view over `src/data/plants.json` for the public (SEO) pages.
 *
 * The editor already consumes the same file through the garden store; this
 * module only adds the lookups and French labels the marketing pages need,
 * so the catalogue stays a single source of truth.
 */
import plantsData from "@/data/plants.json";
import { ZONE_LABELS } from "@/data/climate-zones";
import type { Plant } from "@/types/plants";

export const PLANTS = plantsData as Plant[];

export const MONTHS = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
] as const;

/** URL-safe month segment (`fevrier`, `aout`, …). */
export const MONTH_SLUGS = [
  "janvier",
  "fevrier",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "aout",
  "septembre",
  "octobre",
  "novembre",
  "decembre",
] as const;

export const ZONE_IDS = [1, 2, 3, 4, 5, 6, 7, 8] as const;

export const CATEGORY_LABELS: Record<Plant["category"], string> = {
  legumes: "Légumes",
  aromatiques: "Aromatiques",
  fruits: "Fruits",
  fleurs: "Fleurs compagnes",
};

export const CATEGORY_ORDER: Plant["category"][] = [
  "legumes",
  "aromatiques",
  "fruits",
  "fleurs",
];

export const FAMILY_LABELS: Record<string, string> = {
  solanacee: "Solanacées",
  cucurbitacee: "Cucurbitacées",
  apiacee: "Apiacées (ombellifères)",
  asteracee: "Astéracées",
  legumineuse: "Légumineuses (fabacées)",
  crucifere: "Crucifères (brassicacées)",
  alliacee: "Alliacées",
  chenopodiacee: "Chénopodiacées",
  lamiacee: "Lamiacées",
  rosacee: "Rosacées",
  poacee: "Poacées (graminées)",
  lauracee: "Lauracées",
  tropaeolacee: "Tropaéolacées",
  boraginacee: "Boraginacées",
  violacee: "Violacées",
};

export function familyLabel(family: string): string {
  return FAMILY_LABELS[family] ?? family;
}

export function monthLabel(month: number): string {
  return MONTHS[month - 1] ?? "";
}

export function monthSlug(month: number): string {
  return MONTH_SLUGS[month - 1] ?? "";
}

export function monthFromSlug(slug: string): number | null {
  const index = MONTH_SLUGS.indexOf(slug as (typeof MONTH_SLUGS)[number]);
  return index === -1 ? null : index + 1;
}

export function zoneLabel(zone: number): string {
  return ZONE_LABELS[zone] ?? `Zone ${zone}`;
}

/**
 * French articles for each catalogue entry.
 *
 * Page titles read "Avec quoi associer la tomate ?" and "Calendrier du
 * poireau", so the copy needs gender and elision — neither of which can be
 * inferred from `plants.json`. `label` overrides the display name where the
 * catalogue entry is awkward in a sentence.
 */
type Gender = "m" | "f";
const PLANT_GRAMMAR: Record<string, { gender: Gender; elide?: boolean; label?: string }> = {
  tomate: { gender: "f" },
  courgette: { gender: "f" },
  carotte: { gender: "f" },
  laitue: { gender: "f" },
  "haricot-vert": { gender: "m" },
  poivron: { gender: "m" },
  aubergine: { gender: "f", elide: true },
  concombre: { gender: "m" },
  radis: { gender: "m" },
  oignon: { gender: "m", elide: true },
  ail: { gender: "m", elide: true },
  poireau: { gender: "m" },
  chou: { gender: "m" },
  pois: { gender: "m" },
  epinard: { gender: "m", elide: true, label: "épinard" },
  potiron: { gender: "m" },
  basilic: { gender: "m" },
  persil: { gender: "m" },
  ciboulette: { gender: "f" },
  thym: { gender: "m" },
  menthe: { gender: "f" },
  fraisier: { gender: "m" },
  mais: { gender: "m", label: "maïs doux" },
  fenouil: { gender: "m" },
  sauge: { gender: "f" },
  romarin: { gender: "m" },
  origan: { gender: "m", elide: true },
  coriandre: { gender: "f" },
  aneth: { gender: "m", elide: true },
  estragon: { gender: "m", elide: true },
  "laurier-sauce": { gender: "m" },
  melisse: { gender: "f", label: "mélisse" },
  cerfeuil: { gender: "m" },
  marjolaine: { gender: "f" },
  sarriette: { gender: "f" },
  lavande: { gender: "f" },
  capucine: { gender: "f" },
  souci: { gender: "m", label: "souci" },
  "oeillet-inde": { gender: "m", elide: true, label: "œillet d'Inde" },
  bourrache: { gender: "f" },
  tournesol: { gender: "m" },
  pensee: { gender: "f", label: "pensée" },
  camomille: { gender: "f" },
};

function grammar(plant: Plant) {
  const entry = PLANT_GRAMMAR[plant.id];
  return {
    gender: entry?.gender ?? "m",
    elide: entry?.elide ?? false,
    label: entry?.label ?? plant.name.toLowerCase(),
  };
}

/** "la tomate", "le poireau", "l'ail" */
export function withArticle(plant: Plant): string {
  const { gender, elide, label } = grammar(plant);
  if (elide) return `l'${label}`;
  return `${gender === "f" ? "la" : "le"} ${label}`;
}

/** "de la tomate", "du poireau", "de l'ail" */
export function ofPlant(plant: Plant): string {
  const { gender, elide, label } = grammar(plant);
  if (elide) return `de l'${label}`;
  return `${gender === "f" ? "de la" : "du"} ${label}`;
}

/** "à la tomate", "au poireau", "à l'ail" */
export function toPlant(plant: Plant): string {
  const { gender, elide, label } = grammar(plant);
  if (elide) return `à l'${label}`;
  return `${gender === "f" ? "à la" : "au"} ${label}`;
}

/** Bare lowercase name, accents fixed — "tomate", "épinard", "maïs doux". */
export function plainName(plant: Plant): string {
  return grammar(plant).label;
}

/**
 * "de Romain", but "d'Émilie" — the same elision problem as the plant helpers
 * above, applied to a person's first name or a garden's owner.
 */
export function ofName(name: string): string {
  return /^[aeiouyàâäéèêëîïôöûüh]/i.test(name.trim())
    ? `d'${name}`
    : `de ${name}`;
}

/** "la tomate" -> "La tomate", for sentence starts. */
export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function getPlant(id: string): Plant | undefined {
  return PLANTS.find((plant) => plant.id === id);
}

export function plantName(id: string): string {
  return getPlant(id)?.name ?? id;
}

/** Plants grouped by category, in a stable display order. */
export function plantsByCategory(): { category: Plant["category"]; plants: Plant[] }[] {
  return CATEGORY_ORDER.map((category) => ({
    category,
    plants: PLANTS.filter((plant) => plant.category === category),
  })).filter((group) => group.plants.length > 0);
}

export function plantsByFamily(): { family: string; plants: Plant[] }[] {
  const families = new Map<string, Plant[]>();
  for (const plant of PLANTS) {
    const bucket = families.get(plant.family) ?? [];
    bucket.push(plant);
    families.set(plant.family, bucket);
  }
  return [...families.entries()]
    .map(([family, plants]) => ({ family, plants }))
    .sort((a, b) => b.plants.length - a.plants.length);
}

/** Same family = same soil demands and pests: what rotation is about. */
export function familyMates(plant: Plant): Plant[] {
  return PLANTS.filter((p) => p.family === plant.family && p.id !== plant.id);
}

/**
 * Companion links in `plants.json` are declared one way only. A pairing is
 * just as useful read from the other side, so mirror it here.
 */
export function companionsOf(plant: Plant): Plant[] {
  const ids = new Set(plant.companions);
  for (const other of PLANTS) {
    if (other.companions.includes(plant.id)) ids.add(other.id);
  }
  ids.delete(plant.id);
  return [...ids].map(getPlant).filter((p): p is Plant => Boolean(p));
}

export function incompatiblesOf(plant: Plant): Plant[] {
  const ids = new Set(plant.incompatible);
  for (const other of PLANTS) {
    if (other.incompatible.includes(plant.id)) ids.add(other.id);
  }
  ids.delete(plant.id);
  return [...ids].map(getPlant).filter((p): p is Plant => Boolean(p));
}

/** Zone-specific calendar, falling back to the plant's default months. */
export function scheduleForZone(plant: Plant, zone: number) {
  const zoned = plant.zones_fr?.[String(zone)];
  return {
    sowing: zoned?.sowing ?? plant.sowing_months,
    harvest: zoned?.harvest ?? plant.harvest_months,
  };
}

export function sowsInMonth(plant: Plant, month: number): boolean {
  if (plant.sowing_months.includes(month)) return true;
  return ZONE_IDS.some((zone) => scheduleForZone(plant, zone).sowing.includes(month));
}

export function harvestsInMonth(plant: Plant, month: number): boolean {
  if (plant.harvest_months.includes(month)) return true;
  return ZONE_IDS.some((zone) => scheduleForZone(plant, zone).harvest.includes(month));
}

export function plantsSowableIn(month: number): Plant[] {
  return PLANTS.filter((plant) => sowsInMonth(plant, month));
}

export function plantsHarvestableIn(month: number): Plant[] {
  return PLANTS.filter((plant) => harvestsInMonth(plant, month));
}

/** "mars, avril et mai" — reads better than a comma list in body copy. */
export function formatMonthList(months: number[]): string {
  const labels = [...new Set(months)].sort((a, b) => a - b).map(monthLabel);
  if (labels.length === 0) return "—";
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(", ")} et ${labels[labels.length - 1]}`;
}

/** Rough plants-per-m² from the adult diameter, for the density callout. */
export function plantsPerSquareMeter(plant: Plant): number {
  const spacing = plant.spacing_cm || plant.diameter_cm || 30;
  const perSide = 100 / spacing;
  return Math.max(1, Math.round(perSide * perSide));
}

export function totalCycleDays(plant: Plant): number {
  const { sowing, transplant, harvest } = plant.cycle_days;
  return sowing + transplant + harvest;
}
